import { useCallback, useEffect, useState } from 'react';
import type { AcceptUiPaymentResult, AcceptUiResponse } from '@/types/acceptUi';

interface UseAcceptUiProps {
  environment: 'sandbox' | 'production';
}

interface UseAcceptUiReturn {
  buttonRef: (node: HTMLButtonElement | null) => void;
  scriptLoaded: boolean;
  scriptError: string | null;
  paymentResult: AcceptUiPaymentResult | null;
  reset: () => void;
}

const SCRIPT_ID = 'authorize-net-acceptui';

/**
 * Loads Authorize.net's AcceptUI script and wires up the global responseHandler
 * once a button element is mounted.
 *
 * AcceptUI's script scans the DOM for `class="AcceptUI"` elements at script-load
 * time and binds onclick handlers to each. If the script loads before the button
 * is in the DOM, the click never fires the iframe. This hook captures the button
 * via a state-setter callback ref so the script-injection effect is reactive to
 * the button mounting.
 *
 * On unmount, both the global responseHandler and the script tag are removed so
 * a remount triggers a fresh DOM scan.
 *
 * apiLoginId / publicClientKey are NOT hook props — they're consumed by the
 * button's `data-apiLoginID` / `data-clientKey` attributes (set at the component
 * level), not by the script-loading effect.
 */
export const useAcceptUi = ({ environment }: UseAcceptUiProps): UseAcceptUiReturn => {
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [paymentResult, setPaymentResult] = useState<AcceptUiPaymentResult | null>(null);

  useEffect(() => {
    if (!buttonEl) return;

    window.guardDogAcceptUiHandler = (response: AcceptUiResponse) => {
      if (response.messages.resultCode !== 'Ok') {
        setScriptError(response.messages.message[0]?.text ?? 'Tokenization failed');
        return;
      }
      setPaymentResult(extractResult(response));
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = environment === 'production'
      ? 'https://js.authorize.net/v3/AcceptUI.js'
      : 'https://jstest.authorize.net/v3/AcceptUI.js';
    script.charset = 'utf-8';
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setScriptError('Failed to load payment service. Please try again later.');
    document.body.appendChild(script);

    return () => {
      delete window.guardDogAcceptUiHandler;
      script.remove();
      document.getElementById('AcceptUIContainer')?.remove();
      setScriptLoaded(false);
    };
  }, [buttonEl, environment]);

  const reset = useCallback(() => setPaymentResult(null), []);

  return { buttonRef: setButtonEl, scriptLoaded, scriptError, paymentResult, reset };
};

const extractResult = (response: AcceptUiResponse): AcceptUiPaymentResult => {
  const masked = response.encryptedCardData?.cardNumber ?? '';
  return {
    nonce: response.opaqueData.dataValue,
    cardLast4: masked.slice(-4),
    cardType: deriveCardType(response.encryptedCardData?.bin),
    customerFirstName: response.customerInformation?.firstName,
    customerLastName: response.customerInformation?.lastName,
  };
};

const deriveCardType = (bin?: string): string => {
  if (!bin) return 'Unknown';
  const first = Number(bin[0]);
  const firstTwo = Number(bin.slice(0, 2));
  if (first === 4) return 'Visa';
  if (firstTwo >= 51 && firstTwo <= 55) return 'MasterCard';
  if (firstTwo === 34 || firstTwo === 37) return 'AmericanExpress';
  if (firstTwo === 60 || firstTwo === 65) return 'Discover';
  return 'Unknown';
};
